/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
(function() {
  const prefectureMap = {
    'Hokkaidō': '北海道',
    'Aomori': '青森県',
    'Iwate': '岩手県',
    'Miyagi': '宮城県',
    'Akita': '秋田県',
    'Yamagata': '山形県',
    'Fukushima': '福島県',
    'Ibaraki': '茨城県',
    'Tochigi': '栃木県',
    'Gunma': '群馬県',
    'Saitama': '埼玉県',
    'Chiba': '千葉県',
    'Tōkyō': '東京都',
    'Kanagawa': '神奈川県',
    'Niigata': '新潟県',
    'Toyama': '富山県',
    'Ishikawa': '石川県',
    'Fukui': '福井県',
    'Yamanashi': '山梨県',
    'Nagano': '長野県',
    'Gifu': '岐阜県',
    'Shizuoka': '静岡県',
    'Aichi': '愛知県',
    'Mie': '三重県',
    'Shiga': '滋賀県',
    'Kyōto': '京都府',
    'Ōsaka': '大阪府',
    'Hyōgo': '兵庫県',
    'Nara': '奈良県',
    'Wakayama': '和歌山県',
    'Tottori': '鳥取県',
    'Shimane': '島根県',
    'Okayama': '岡山県',
    'Hiroshima': '広島県',
    'Yamaguchi': '山口県',
    'Tokushima': '徳島県',
    'Kagawa': '香川県',
    'Ehime': '愛媛県',
    'Kōchi': '高知県',
    'Fukuoka': '福岡県',
    'Saga': '佐賀県',
    'Nagasaki': '長崎県',
    'Kumamoto': '熊本県',
    'Ōita': '大分県',
    'Miyazaki': '宮崎県',
    'Kagoshima': '鹿児島県',
    'Okinawa': '沖縄県'
  };

  function convertPrefecturesToJapanese(selectElement) {
      Array.from(selectElement.options).forEach(option => {
          if (option.value in prefectureMap) {
            option.text = prefectureMap[option.value];
          }else{
            option.text = '都道府県を選択してください';
          }
      });
  }

  function handleIntersection(entries, observer) {
      entries.forEach(entry => {
          if (entry.isIntersecting) {
              convertPrefecturesToJapanese(entry.target);
              observer.unobserve(entry.target);
          }
      });
  }

  function observeSelectBox(selectElement) {
      const intersectionObserver = new IntersectionObserver(handleIntersection, {
          root: null,
          rootMargin: '0px',
          threshold: 0.1
      });

      intersectionObserver.observe(selectElement);
  }

  function moveElement() {
      const elementToMove = document.querySelector('.js-appendCfApp');
      const targetElement = document.querySelector('.c_bonify.c_agree');

      if (elementToMove && targetElement) {
          // hidden属性を削除
          elementToMove.removeAttribute('hidden');
          // 要素を移動
          targetElement.appendChild(elementToMove);          
      } else {
          console.error('要素が見つかりません');
      }
  }

  function observeDOMChanges() {
      const targetNode = document.body;
      const config = { childList: true, subtree: true };

      const callback = function(mutationsList, observer) {
          for(let mutation of mutationsList) {
              if (mutation.type === 'childList') {
                  const selectElement = document.getElementById('customer-address-province');
                  if (selectElement) {
                      observeSelectBox(selectElement);
                      moveElement(); // 要素の移動とhidden属性の削除を実行
                      observer.disconnect();
                      break;
                  }
              }
          }
      };

      const mutationObserver = new MutationObserver(callback);
      mutationObserver.observe(targetNode, config);
  }

  observeDOMChanges();
})();


// Kimonixのセクションにおいてお気に入りボタンのクリック時の伝播を防ぐ
(function() {  
    // メイン関数: ウィッシュリストボタンのクリック伝播を防ぐ
    function preventWishlistPropagation(button) {
      button.addEventListener('click', function(event) {
        event.preventDefault();  // デフォルトの動作を防ぐ
        event.stopPropagation(); // イベントの伝播を停止
      });
    }
    // IntersectionObserver インスタンスを作成
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            preventWishlistPropagation(entry.target);
            intersectionObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        root: null,
        rootMargin: '0px'
      }
    );

    function observeNewElement(element) {
      if (element.matches('.grid-item-kmnx wishlist-button-collection')) {
        intersectionObserver.observe(element);
      } else {
        element.querySelectorAll('.grid-item-kmnx wishlist-button-collection').forEach(button => {
          intersectionObserver.observe(button);
        });
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              observeNewElement(node);
            }
          });
        }
      });
    });
  
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  
    document.querySelectorAll('.grid-item-kmnx wishlist-button-collection').forEach(button => {
      intersectionObserver.observe(button);
    });
  
})();

// 新規会員登録 国:Japan→日本語に変更
function customerFieldsAppReady() {
  return new Promise((resolve) => {
    const checkInterval = setInterval(() => {
      if (typeof window['CustomerFieldsApp'] !== 'undefined') {
        clearInterval(checkInterval);
        resolve(window['CustomerFieldsApp']);
      }
    }, 100);
  });
}

customerFieldsAppReady().then((cf_app) => {
  cf_app.settings.onFieldsLoaded = function (form) {
    console.log('onFieldsLoaded');
    const countrySelect = form.querySelector('#customer-address-country');
    if (countrySelect) {
      const japanOption = countrySelect.querySelector('option[value="Japan"]');
      if (japanOption) {
        japanOption.textContent = '日本';
      }
    }
  };
});